import { useEffect } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'

export default function Privacy() {
  useEffect(() => {
    document.body.classList.add('v2-legal-page')
    return () => document.body.classList.remove('v2-legal-page')
  }, [])

  return (
    <>
      <SEO
        title="Privacy Policy - opendeploy.dev"
        description="opendeploy.dev privacy policy. How we collect, use, and protect your data on our agent-first deployment platform."
        path="/privacy"
      />

      <div className="od-home od-legal-shell">
        <SiteNav />

        <main className="od-legal-main">
          <header className="od-legal-hero">
            <h1>Privacy Policy</h1>
            <p>Last updated: April 27, 2026</p>
          </header>

          <div className="od-legal-sections">
            <section className="od-legal-section">
              <h2>1. Introduction</h2>
              <p>
                opendeploy.dev ("we," "our," or "us") operates the opendeploy.dev website, CLI, MCP server, IDE plugins, and deployment platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you — or an AI coding agent acting on your behalf — use our services. By accessing or using our platform, you agree to this Privacy Policy.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>2. Information We Collect</h2>
              <h3>2.1 Information You Provide</h3>
              <ul>
                <li>Account information: name, email address, and password (or magic-link identifier) when you register.</li>
                <li>Payment information: billing details processed through our third-party payment provider. We do not store your full credit card number.</li>
                <li>Project data: source code, configuration files, and other content you (or your agent) push for deployment.</li>
                <li>Agent metadata: agent names, token labels, and the platform identifier (e.g. <code>claude-code</code>, <code>cursor</code>, <code>codex</code>) you supply when issuing tokens.</li>
                <li>Communications: messages you send to us via email, Discord, or support channels.</li>
              </ul>
              <h3>2.2 Information Collected Automatically</h3>
              <ul>
                <li>Usage data: deployments performed, build and runtime logs, project state changes, dashboard pages visited, and CLI/MCP commands invoked.</li>
                <li>Agent activity: which token initiated a deployment, the action taken, exit status, resource usage, and timestamps. This is what populates the audit log on the dashboard.</li>
                <li>AI gateway telemetry: when you route AI calls through opendeploy.dev's gateway (BYOK or otherwise), we record provider, model, token counts, and cost — but not prompt or completion content unless you opt in to debug logging.</li>
                <li>Device information: browser type, operating system, IP address, and device identifiers for dashboard sessions; CLI version, OS, and machine hostname for CLI sessions.</li>
                <li>Cookies and similar technologies: we use cookies on opendeploy.dev to maintain sessions and improve your experience.</li>
              </ul>
            </section>

            <section className="od-legal-section">
              <h2>3. How We Use Your Information</h2>
              <ul>
                <li>Provide, maintain, and improve our agent-first deployment platform.</li>
                <li>Process deployments initiated by you or by agents holding your tokens, including building, scheduling, and running your application.</li>
                <li>Render observability surfaces: deploy history, spend, logs, vitals, and audit trail of agent actions.</li>
                <li>Process payments and manage your subscription, plan, and metered usage.</li>
                <li>Send service-related communications, such as deployment status updates, budget-cap alerts, token activity warnings, and security notices.</li>
                <li>Respond to your inquiries and provide customer support.</li>
                <li>Detect, prevent, and address technical issues, abuse, fraud, or security threats — including unusual agent behavior that may indicate a compromised token or prompt-injection attack.</li>
                <li>Comply with legal obligations.</li>
              </ul>
              <p>
                We do not use your source code, prompts, or model outputs to train any AI model — ours or third-party.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>4. Source Code, Project Data, and Agent Artifacts</h2>
              <p>
                Your source code is pushed solely for the purpose of building and deploying your application. We do not sell, share, or use your source code for any purpose other than providing our deployment services. Source code is encrypted in transit and at rest, and is not retained longer than necessary for deployment. Build artifacts are stored only for the duration of your active deployment.
              </p>
              <p>
                Artifacts produced by AI coding agents — generated configuration, Dockerfiles, build commands, environment variable suggestions — are treated with the same protections as source code. Where the agent runs in your local environment (for example, Claude Code on your machine), its prompts and intermediate outputs never leave your device unless the agent itself transmits them; opendeploy.dev only sees what the agent submits to our API.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>5. Data Sharing and Disclosure</h2>
              <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
              <ul>
                <li><strong>Service providers:</strong> trusted third parties that assist us in operating our platform (cloud infrastructure, payment processing, analytics, email delivery).</li>
                <li><strong>AI model providers:</strong> when you route AI calls through our gateway with BYOK keys, request payloads pass to the provider you selected (Anthropic, OpenAI, Google, xAI, etc.) under that provider's privacy terms.</li>
                <li><strong>Legal requirements:</strong> when required by law, subpoena, or government request.</li>
                <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets, with notice to affected users.</li>
                <li><strong>With your consent:</strong> when you explicitly authorize us to share information.</li>
              </ul>
              <p>
                We do not share data with third-party AI agent vendors (the makers of the coding agents that deploy on your behalf). The agent connects to opendeploy.dev with your token; we do not relay your project data back to the agent's vendor.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>6. Data Security</h2>
              <p>
                We implement industry-standard security measures including encryption (TLS 1.2+), isolated container environments, network policies, encrypted storage, scoped agent tokens with revocation, optional spend caps, and regular security audits. While we strive to protect your data, no method of transmission or storage is 100% secure.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>7. Data Retention</h2>
              <p>
                We retain your account information for as long as your account is active. Deployment data, logs, and the agent activity audit trail are retained for the duration of your subscription. If your account is terminated or payment fails, your data will be retained for 7 days before permanent deletion. You may request deletion of your account and associated data at any time by contacting us.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>8. Your Rights</h2>
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul>
                <li>Access, correct, or delete your personal information.</li>
                <li>Object to or restrict processing of your data.</li>
                <li>Export your data — including the agent activity audit trail — in a portable format.</li>
                <li>Withdraw consent where processing is based on consent.</li>
                <li>Revoke any agent token at any time from the dashboard.</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at <a href="mailto:hi@opendeploy.dev">hi@opendeploy.dev</a>.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>9. Cookies</h2>
              <p>
                We use essential cookies on opendeploy.dev to maintain your session and authenticate your account. We may also use analytics cookies to understand how the dashboard is used. You can control cookie preferences through your browser settings. The CLI and MCP server do not use cookies.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>10. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and updating the "Last updated" date. Your continued use of the platform after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="od-legal-section">
              <h2>12. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy or our data practices, please contact us at <a href="mailto:hi@opendeploy.dev">hi@opendeploy.dev</a>.
              </p>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  )
}
